import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageSquarePlus, FileUp, RefreshCw, History, CheckCircle2, CircleDot, Eye } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { fmtSize } from "@/components/AttachmentsPanel";
import { AI_MODEL_LABELS } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RevisionPanel({ tender, canManage, onRevise, streaming }) {
  const [comments, setComments] = useState([]);
  const [versions, setVersions] = useState([]);
  const [revisionDocs, setRevisionDocs] = useState([]);
  const [text, setText] = useState("");
  const [uploading, setUploading] = useState(false);
  const [viewVersion, setViewVersion] = useState(null);
  const inputRef = useRef(null);

  const openVersion = async (version) => {
    try {
      const { data } = await axios.get(`${API}/tenders/${tender.id}/doc-versions/${version}`);
      setViewVersion(data);
    } catch {
      toast.error("Could not load the archived draft.");
    }
  };

  const load = useCallback(() => {
    axios.get(`${API}/tenders/${tender.id}/doc-comments`).then((r) => setComments(r.data));
    axios.get(`${API}/tenders/${tender.id}/doc-versions`).then((r) => setVersions(r.data));
    axios.get(`${API}/tenders/${tender.id}/attachments`).then((r) => setRevisionDocs(r.data.filter((a) => a.category === "revision")));
  }, [tender.id]);
  useEffect(() => { load(); }, [load, tender.document_version, streaming]);

  const addComment = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      await axios.post(`${API}/tenders/${tender.id}/doc-comments`, { comment: text });
      toast.success("Review comment captured.");
      setText("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add comment.");
    }
  };

  const uploadRevisionDocs = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("category", "revision");
      try {
        await axios.post(`${API}/tenders/${tender.id}/attachments`, fd);
      } catch (err) {
        toast.error(`${f.name}: ${err.response?.data?.detail || "upload failed"}`);
      }
    }
    setUploading(false);
    e.target.value = "";
    load();
  };

  const openCount = comments.filter((c) => c.status === "open").length;

  if (streaming) return null;

  return (
    <div className="border-t border-slate-200 bg-slate-50 px-8 py-6" data-testid="revision-panel">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-5">
        <div>
          <h3 className="font-serif text-xl text-slate-900">Draft Review & Revision</h3>
          <p className="text-xs text-slate-500 mt-1">
            Capture comments and upload supporting documents — then generate a revised draft that addresses them all.
          </p>
        </div>
        {canManage && (
          <button
            onClick={onRevise}
            disabled={openCount === 0 && revisionDocs.length === 0}
            data-testid="generate-revision-btn"
            className="inline-flex items-center gap-2 bg-[#0050A0] text-white px-6 py-3 text-sm font-semibold hover:bg-[#003F7A] transition-colors duration-200 disabled:opacity-50 shrink-0"
            title={openCount === 0 && revisionDocs.length === 0 ? "Add a comment or revision document first" : ""}
          >
            <RefreshCw className="w-4 h-4" aria-hidden="true" /> Generate Revised Draft (v{(tender.document_version || 1) + 1})
          </button>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-6 items-start">
        <div>
          {canManage && (
            <form onSubmit={addComment} className="mb-4">
              <Textarea data-testid="revision-comment-input" className="bg-white border-slate-300 min-h-[70px]" placeholder="e.g., Tighten the eligibility criteria in Section 3; add a 2% bid security clause…" value={text} onChange={(e) => setText(e.target.value)} />
              <button type="submit" data-testid="add-comment-btn" className="mt-2 inline-flex items-center gap-2 bg-slate-900 text-white px-4 py-2 text-xs font-semibold hover:bg-slate-700 transition-colors duration-200">
                <MessageSquarePlus className="w-3.5 h-3.5" aria-hidden="true" /> Add Comment
              </button>
            </form>
          )}
          {comments.length === 0 ? (
            <p className="text-xs text-slate-400" data-testid="comments-empty">No review comments yet.</p>
          ) : (
            <ul className="space-y-2">
              {comments.map((c, i) => (
                <li key={c.id} className="bg-white border border-slate-200 p-3" data-testid={`doc-comment-${i}`}>
                  <div className="flex items-start gap-2">
                    {c.status === "open" ? (
                      <CircleDot className="w-3.5 h-3.5 text-amber-500 mt-0.5 shrink-0" aria-hidden="true" />
                    ) : (
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" aria-hidden="true" />
                    )}
                    <div className="min-w-0">
                      <p className="text-sm text-slate-800 leading-snug">{c.comment}</p>
                      <p className="text-[11px] text-slate-400 mt-1">
                        {c.author} · on v{c.draft_version} ·{" "}
                        {c.status === "open" ? <span className="text-amber-600 font-medium">open</span> : <span className="text-emerald-600 font-medium">addressed in v{c.addressed_in_version}</span>}
                      </p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="space-y-5">
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400 flex items-center gap-1.5">
                <FileUp className="w-3.5 h-3.5" aria-hidden="true" /> Revision Support Documents
              </p>
              {canManage && (
                <>
                  <input ref={inputRef} type="file" multiple accept=".pdf,.docx,.txt,.md,.csv" className="hidden" onChange={uploadRevisionDocs} data-testid="revision-file-input" />
                  <button onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="upload-revision-doc-btn" className="border border-slate-300 text-slate-600 px-3 py-1.5 text-xs font-semibold hover:bg-slate-900 hover:text-white transition-colors duration-200 disabled:opacity-60">
                    {uploading ? "Uploading…" : "Upload"}
                  </button>
                </>
              )}
            </div>
            {revisionDocs.length === 0 ? (
              <p className="text-xs text-slate-400">None yet — attach material that should shape the next revision.</p>
            ) : (
              <ul className="space-y-1">
                {revisionDocs.map((a, i) => (
                  <li key={a.id} className="text-xs text-slate-600 bg-white border border-slate-200 px-3 py-2 flex justify-between" data-testid={`revision-doc-${i}`}>
                    <span className="truncate">{a.filename}</span>
                    <span className="font-mono text-slate-400 ml-3 shrink-0">{fmtSize(a.size)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400 flex items-center gap-1.5 mb-2">
              <History className="w-3.5 h-3.5" aria-hidden="true" /> Version History
            </p>
            <ul className="space-y-1" data-testid="version-history">
              <li className="text-xs text-slate-700 bg-white border border-[#0050A0]/30 px-3 py-2 flex justify-between">
                <span className="font-semibold">v{tender.document_version || 1} — current draft</span>
                <span className="font-mono text-slate-400">{tender.document_generated_at?.slice(0, 10)}</span>
              </li>
              {versions.map((v, i) => (
                <li key={v.id}>
                  <button
                    onClick={() => openVersion(v.version)}
                    data-testid={`view-version-${v.version}`}
                    className="w-full text-xs text-slate-500 bg-white border border-slate-200 px-3 py-2 flex justify-between items-center hover:border-[#0050A0] hover:text-[#0050A0] transition-colors duration-200"
                  >
                    <span className="inline-flex items-center gap-1.5">
                      <Eye className="w-3 h-3" aria-hidden="true" /> v{v.version} — archived · open to read
                    </span>
                    <span className="font-mono text-slate-400">{v.archived_at?.slice(0, 10)}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <Dialog open={!!viewVersion} onOpenChange={(o) => !o && setViewVersion(null)}>
        <DialogContent className="rounded-none max-w-4xl" data-testid="version-viewer-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif">
              Archived Draft — v{viewVersion?.version}
              <span className="text-sm font-normal text-slate-400 ml-3">
                {viewVersion?.model && `${AI_MODEL_LABELS[viewVersion.model] || viewVersion.model} · `}
                archived {viewVersion?.archived_at?.slice(0, 10)} · read-only
              </span>
            </DialogTitle>
          </DialogHeader>
          <div className="doc-prose max-h-[65vh] overflow-y-auto pr-4" data-testid="version-viewer-content">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{viewVersion?.document || ""}</ReactMarkdown>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
